/**
 * TINTED PIECES — the editor / preview / print side of worker/src/lib/themes/tint.ts
 * (owner, 2026-10-14: « Collage anniversaire » 4-10 — the admin chooses its colours).
 *
 * A tintable piece's PNG / JPG is a COLOUR MAP: each pixel's red, green and blue are the
 * shares of the album's accent, second colour and light; the rest is the deep colour.
 * The element (or the page background) carries the four colours, `tint: [D, A, B, L]`:
 *
 *     out = D + r·(A − D) + g·(B − D) + b·(L − D)      — each channel, clamped; alpha kept
 *
 * ONE matrix, two drawers:
 *   · editor + preview — an SVG feColorMatrix (colour-interpolation sRGB, which works on
 *     the non-premultiplied colour, as getImageData does) applied with CSS `filter: url()`
 *     (components/page/TintFilter.tsx);
 *   · print — the same matrix on the piece's pixels on a canvas, before it is embedded
 *     (tintPixels, pdfGenerator `tintedPiece`).
 * tintParity.test.ts holds the two together and to the worker's copy.
 */

/** The four colours, matrix order: deep, accent, second, light. */
export type TintStops = readonly string[];

function rgb01(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** A usable tint: four #RRGGBB colours. Anything else draws the piece as it is. */
export function isTint(t: unknown): t is TintStops {
  return Array.isArray(t) && t.length === 4 && t.every((c) => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c));
}

/** The 4 × 5 colour matrix (feColorMatrix `values`, row by row) — worker tint.ts tintMatrix. */
export function tintMatrix(stops: TintStops): number[] {
  const [D, A, B, L] = stops.map(rgb01);
  const rows: number[] = [];
  for (let c = 0; c < 3; c++) rows.push(A[c] - D[c], B[c] - D[c], L[c] - D[c], 0, D[c]);
  rows.push(0, 0, 0, 1, 0);
  return rows.map((v) => Math.round(v * 1e6) / 1e6);
}

/** The SVG filter's `values` attribute. */
export function tintMatrixValues(stops: TintStops): string {
  return tintMatrix(stops).join(' ');
}

/** A stable DOM id for the filter of these colours (one per tint, shared by every piece in it). */
export function tintFilterId(stops: TintStops): string {
  return `tint-${stops.map((c) => c.replace('#', '').toLowerCase()).join('')}`;
}

/**
 * The print: recolour RGBA pixels in place with the matrix (the SVG filter's arithmetic on
 * 0–1 values, clamped, rounded to 0–255). Alpha untouched; fully transparent pixels skipped.
 */
export function tintPixels(data: Uint8ClampedArray, stops: TintStops): void {
  const M = tintMatrix(stops);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    const r = data[i] / 255, g = data[i + 1] / 255, b = data[i + 2] / 255;
    for (let c = 0; c < 3; c++) {
      const v = M[c * 5] * r + M[c * 5 + 1] * g + M[c * 5 + 2] * b + M[c * 5 + 4];
      data[i + c] = Math.round(255 * (v < 0 ? 0 : v > 1 ? 1 : v));
    }
  }
}

/** One pixel's colour in the tint (tests, tone checks): a recipe (r, g, b shares 0–1) → #RRGGBB. */
export function tintedColour(mix: readonly number[], stops: TintStops): string {
  const px = new Uint8ClampedArray([Math.round(mix[0] * 255), Math.round(mix[1] * 255), Math.round(mix[2] * 255), 255]);
  tintPixels(px, stops);
  return '#' + [px[0], px[1], px[2]].map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** The cache key of a tinted piece in the PDF: the file and its colours (each tint embedded once). */
export function tintKey(url: string, stops: TintStops): string {
  return `${url}|tint|${stops.join('')}`;
}
